import { appLogger } from '@tn/foundation/utils/app-logger/appLogger';
import { USER_UUID_COOKIE_NAME } from '@tn/foundation/utils/constants/common';
import { setUserUUIDCookie } from '@tn/foundation/utils/cookies/setUserUUIDCookie';
import { hasCookie } from 'cookies-next';
import request from 'graphql-request';
import {
    AnyEventObject,
    AnyStateConfig,
    assertEvent,
    DoneActorEvent,
    enqueueActions,
    ErrorActorEvent,
    fromPromise,
    Mapper,
    setup,
} from 'xstate';

import { FlowEntity, InitMachineParams } from '../../types';

import { mosaicPageDocumentsCacheKey, mosaicPageDocumentsQuery } from './graphql/constants';
import {
    ResolveUpsellDocumentsEvent,
    UpsellDocumentsLookup,
    UpsellDocumentsResolution,
    UpsellLookupRequest,
} from './documents';

type LookupInput = UpsellLookupRequest;

type Destinations = 'signIn' | 'signUp' | 'mvpd';
export type UpsellFlowContext = {
    destinationState?: Destinations;
    /** The last request the Mosaic body sent; the resolution carries it once settled. */
    request: LookupInput;
    /** Null until the lookup settled. */
    resolution: UpsellDocumentsResolution | null;
};

const SIGNUP_GRAPHQL_ENDPOINT = '/api/tn/signup/graphql/';

/**
 * Refetches the Mosaic documents with an experiment lookup for a visitor the
 * server could not bucket: an anonymous response is CDN-cached, so the route
 * resolved the blueprint defaults with `variants: false`. The request leaves
 * the browser with the visitor's own `ovvuid`, the id the page's variants call
 * minted, so the gateway buckets both the same way. The event is the whole
 * request (`upsellLookupRequest`): page, campaign and the location's targeting.
 */
export const resolveUpsellDocumentsActor: FlowEntity = {
    initActor: ({ queryClient }) =>
        fromPromise<UpsellDocumentsLookup, LookupInput>(async ({ input }) => {
            const variables = { ...input, variants: true };
            // The gateway buckets on the `ovvuid` cookie. The experiments provider
            // mints it in a passive effect of its own; make sure it exists before
            // this request leaves, whichever effect React runs first.
            if (!hasCookie(USER_UUID_COOKIE_NAME)) setUserUUIDCookie();
            const result = await queryClient.fetchQuery({
                queryKey: [mosaicPageDocumentsCacheKey, variables],
                queryFn: () =>
                    request(SIGNUP_GRAPHQL_ENDPOINT, mosaicPageDocumentsQuery, variables, {
                        'request-source': mosaicPageDocumentsCacheKey,
                    }),
            });
            const documents = result.mosaicPageDocuments;
            return {
                // A payload without a page (unknown surface, the resolver's own blueprint
                // read failed) is no resolution for the documents, but the bucket it
                // reports is still an exposure DirectAB assigned.
                documents: documents?.page ? documents : null,
                variantsForTracking: documents?.variantsForTracking ?? [],
            };
        }),

    // The invoke runs on the `RESOLVE_DOCUMENTS` transition, so the event is the request.
    inputActor: <Mapper<UpsellFlowContext, AnyEventObject, LookupInput, AnyEventObject>>(
        (({ event }) => (event as ResolveUpsellDocumentsEvent).params)
    ),
};

/**
 * The dtc machine's child on `/`. Idle until the Mosaic body the server
 * rendered without an experiment lookup asks for one (`RESOLVE_DOCUMENTS`);
 * `resolving` runs the lookup, `ready` holds the result, success or not. The
 * states live in the blueprint (`upsell.jsonc`); the page follows the snapshot
 * through `selectUpsellDocuments`.
 */
export const upsellFlow: FlowEntity = {
    initActor: (rawParams) => {
        const { config, actors } = rawParams as InitMachineParams;
        return setup({
            types: {
                context: {} as UpsellFlowContext,
                events: {} as
                    | ResolveUpsellDocumentsEvent
                    | DoneActorEvent<UpsellDocumentsLookup, 'resolveDocuments'>
                    | ErrorActorEvent<unknown, 'resolveDocuments'>,
            },
            guards: {},
            actions: {
                rememberRequest: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'RESOLVE_DOCUMENTS');
                    enqueue.assign({ request: event.params });
                }),
                saveDocuments: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, 'xstate.done.actor.resolveDocuments');
                    enqueue.assign({ resolution: { ...event.output, request: context.request } });
                }),
                // The lookup failed: the server defaults stay and no exposure counts.
                keepServerDocuments: enqueueActions(({ enqueue, context }) => {
                    // The direct `graphql-request` call bypasses the shared request logger:
                    // a static message and identifiers only, never the upstream error text.
                    appLogger.error('Mosaic upsell documents lookup failed', {
                        component: 'resolveUpsellDocumentsActor',
                        cacheKey: mosaicPageDocumentsCacheKey,
                        errorCode: 'MOSAIC_DOCUMENTS_LOOKUP_FAILED',
                        pageId: context.request.pageId,
                    });
                    enqueue.assign({
                        resolution: {
                            documents: null,
                            variantsForTracking: [],
                            request: context.request,
                        },
                    });
                }),
            },
            actors,
        }).createMachine({
            ...(config as AnyStateConfig),
            context: {
                request: { pageId: '', cbscidmt: '', promo: '', flowType: '' },
                resolution: null,
            },
        });
    },
};
